import React, { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { TaskTemplateInputGroupType } from "api/model/enums";
import {
  TaskTemplateInputGroupDefinition,
  TaskTemplateInputFromSubgroup,
  TaskTemplateDefinitionDTO,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import InputGroupedDefinitionsSubFormsList from "./InputGroupedDefinitionsSubFormsList";
import { createObject, handleMiniDialogCancel } from "./TemplateEditorUtils";
import "./TemplateEditor.css";

interface TemplateEditorProps {
  initialData?: string;
  handleDefChange?: (data: TaskTemplateDefinitionDTO) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ initialData, handleDefChange }) => {
  const initialDataJson = initialData ? JSON.parse(initialData) : {};
  const [inputGroupDefinitions, setInputGroupDefinitions] = useState<
    TaskTemplateInputGroupDefinition[]
  >(initialDataJson?.inputGroupDefinitions || []);
  const [selectedGroup, setSelectedGroup] = useState<TaskTemplateInputGroupDefinition | null>(null);
  const [newGroupId, setNewGroupId] = useState<string>("");
  const [newGroupHeader, setNewGroupHeader] = useState<string>("");
  const [miniDialog, setMiniDialog] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempId, setTempId] = useState<string>("");

  const taskTemplateInputGroupOptions = Object.keys(TaskTemplateInputGroupType).map(key => ({
    label: TaskTemplateInputGroupType[key as keyof typeof TaskTemplateInputGroupType],
    value: TaskTemplateInputGroupType[key as keyof typeof TaskTemplateInputGroupType],
  }));

  const deleteTemplate = (id: string) => {
    setInputGroupDefinitions(prevTemplates => {
      const updatedTemplates = [...prevTemplates];
      const templateToDelete = updatedTemplates.find(template => template.id === id);
      if (templateToDelete) {
        updatedTemplates.splice(updatedTemplates.indexOf(templateToDelete), 1);
      }
      return updatedTemplates;
    });
  };

  const updateTemplate = (
    id: string,
    key: keyof TaskTemplateInputGroupDefinition,
    value: string
  ) => {
    setInputGroupDefinitions(prevTemplates => {
      return prevTemplates.map(template => {
        if (template.id === id) {
          const updatedTemplates = { ...template, [key]: value };
          if (selectedGroup && selectedGroup.id === id) {
            setSelectedGroup(updatedTemplates);
          }
          return updatedTemplates;
        }
        return template;
      });
    });
  };

  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-popup.are-you-sure-you-want-to-delete-this-input-grouped-definition"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => deleteTemplate(id),
    });
  };

  const confirmOnIdChange = (newId: string) => {
    confirmPopup({
      message: T("confirm-popup.are-you-sure-you-want-to-change-the-id"),
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        if (selectedGroup && !inputGroupDefinitions.find(group => group.id === newId)) {
          updateTemplate(selectedGroup.id, "id", newId);
          setEditingId(null);
          easasAlert({
            severity: "success",
            summary: T("alert.id-changet-to", { id: newId }),
          });
        } else {
          easasAlert({
            severity: "error",
            summary: T("error.id-already-exists-please-choose-a-different-id", { id: newId }),
          });
        }
      },
    });
  };

  const onSubFormsChange = (newSubForms: TaskTemplateInputFromSubgroup[]) => {
    if (selectedGroup) {
      const updatedGroup: TaskTemplateInputGroupDefinition = {
        ...selectedGroup,
        subForms: newSubForms,
      };
      setSelectedGroup(updatedGroup);
      setInputGroupDefinitions(prevDefinitions => {
        return prevDefinitions.map(group => (group.id === updatedGroup.id ? updatedGroup : group));
      });
    }
  };

  const handleSave = () => {
    if (handleDefChange) {
      handleDefChange({ inputGroupDefinitions });
    }
  };

  const handleMiniDialogSubmit = () => {
    const currentIds = inputGroupDefinitions.map(group => group.id);
    if (currentIds.includes(newGroupId)) {
      easasAlert({
        severity: "error",
        summary: T("error.input-id-already-exists-please-choose-a-different-id", { newId: newGroupId }),
      });

      return;
    }
    setInputGroupDefinitions(prevGroups => {
      const newGroup: TaskTemplateInputGroupDefinition = {
        id: newGroupId,
        type: TaskTemplateInputGroupType.SIMPLE,
        header: newGroupHeader,
        subForms: [],
      };
      return [...prevGroups, newGroup];
    });
    setMiniDialog(false);
    setNewGroupId("");
    setNewGroupHeader("");
  };

  const onRowReorder = (e: { value: TaskTemplateInputGroupDefinition[] }) => {
    setInputGroupDefinitions(e.value);
  };

  const renderIdInput = (
    rowData: TaskTemplateInputGroupDefinition,
    isTable: boolean,
    confirmOnChange?: (value: string) => void
  ) => (
    <div>
      {isTable ? (
        <div className="flex flex-row">
          <div>{rowData.id}</div>
        </div>
      ) : (
        <div>
          <InputText
            placeholder="Id"
            value={editingId === rowData.id ? tempId : rowData.id}
            onChange={e => {
              setEditingId(rowData.id);
              setTempId(e.target.value);
            }}
            onClick={() => setSelectedGroup(rowData)}
            className="p-inputtext-sm"
          />
          {selectedGroup && editingId === selectedGroup.id && confirmOnChange && (
            <Button
              label={T("action.update")}
              className="p-button-sm p-button-primary w-5rem"
              onClick={() => confirmOnChange(tempId)}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderHeaderInput = (rowData: TaskTemplateInputGroupDefinition, isTable: boolean) => (
    <div>
      {isTable ? (
        <div>{rowData.header}</div>
      ) : (
        <InputText
          placeholder={T("word.header")}
          value={rowData.header}
          onChange={e => updateTemplate(rowData.id, "header", e.target.value)}
          className="p-inputtext-sm"
        />
      )}
    </div>
  );

  const renderDeleteButton = (rowData: TaskTemplateInputGroupDefinition) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={e => confirmOnDelete(e, rowData.id)}
    />
  );

  const renderDuplicateButton = (rowData: TaskTemplateInputGroupDefinition) => (
    <Button
      icon="pi pi-copy"
      tooltip={T("action.duplicate")}
      className="p-button-rounded p-button-text p-button-secondary"
      onClick={() => handleDuplicate(rowData)}
    />
  );

  const handleDuplicate = (rowData: TaskTemplateInputGroupDefinition) => {
    let newId = `${rowData.id}_1`;
    for (let i = 0; i < inputGroupDefinitions.length; i++) {
      if (inputGroupDefinitions[i].id === newId) {
        newId = `${newId}_1`;
        i = 0;
      }
    }
    const newGroup: TaskTemplateInputGroupDefinition = {
      ...rowData,
      id: newId,
    };
    setInputGroupDefinitions(prevGroups => [...prevGroups, newGroup]);
  };

  return (
    <div>
      <Button
        className="p-button-success w-max mb-3"
        label={T("action.add-group")}
        onClick={() => createObject(setMiniDialog)}
      />
      <DataTable
        metaKeySelection={false}
        header={T("word.groups")}
        value={inputGroupDefinitions}
        reorderableRows
        onRowReorder={onRowReorder}
        editMode="cell"
        dataKey="id"
        selection={selectedGroup}
        onSelectionChange={e => {
          if (selectedGroup?.id === e.value?.id) {
            setSelectedGroup(null);
          } else {
            setSelectedGroup(e.value);
          }
        }}
        responsiveLayout="scroll"
        scrollHeight="300px"
        size="small"
        className="p-0"
      >
        <Column selectionMode="single" style={{ width: "2%", padding: "0.3rem" }} />
        <Column
          field="id"
          style={{ minWidth: "100px", padding: 0 }}
          className="overflow-hidden text-overflow-ellipsis"
          header="Id"
          body={rowData => renderIdInput(rowData, true)}
        />
        <Column
          field="header"
          header={T("word.header")}
          style={{ width: "56%", padding: 0 }}
          body={rowData => renderHeaderInput(rowData, true)}
        />
        <Column style={{ width: "4%", padding: 0 }} body={rowData => renderDeleteButton(rowData)} />
        <Column
          style={{ width: "5%", padding: 0 }}
          body={rowData => renderDuplicateButton(rowData)}
        />
        <Column rowReorder style={{ justifyContent: "end", width: "5%", padding: 0 }} />
      </DataTable>
      {selectedGroup && (
        <div className="subform-container mt-4">
          <h4>{T("word.group")} {selectedGroup.header}</h4>
          <div className="flexRow">
            <div className="p-field p-col-4">
              <label>Id:</label>
              {renderIdInput(selectedGroup, false, confirmOnIdChange)}
            </div>
            <div className="p-field p-col-4">
              <label htmlFor="type">{T("word.type")}:</label>
              <Dropdown
                value={selectedGroup.type}
                options={taskTemplateInputGroupOptions}
                filter
                showClear
                onChange={e => updateTemplate(selectedGroup.id, "type", e.value)}
                optionLabel="label"
                placeholder={T("action.select-a-type")}
                className="p-inputtext-sm"
              />
            </div>
            <div className="p-field p-col-4">
              <label>{T("word.header")}:</label>
              <InputText
                placeholder={T("word.header")}
                value={selectedGroup.header}
                onChange={e => updateTemplate(selectedGroup.id, "header", e.target.value)}
                className="p-inputtext-sm"
              />
            </div>
          </div>
          <InputGroupedDefinitionsSubFormsList
            subForms={selectedGroup.subForms}
            onSubFormsChange={newSubForms => onSubFormsChange(newSubForms)}
          />
        </div>
      )}
      <div className="field col-12 mt-5">
        <Button
          label={T("action.apply-template")}
          className="p-button-success"
          icon="pi pi-check"
          onClick={handleSave}
        />
      </div>
      <Dialog
        header={T("action.create-new-group")}
        blockScroll
        visible={miniDialog}
        onHide={() => setMiniDialog(false)}
        footer={
          <div>
            <Button
              label={T("word.cancel")}
              icon="pi pi-times"
              onClick={() =>
                handleMiniDialogCancel(setMiniDialog, setNewGroupId, setNewGroupHeader)
              }
              className="p-button-sm p-button-danger"
            />
            <Button
              label={T("action.create")}
              icon="pi pi-check"
              onClick={handleMiniDialogSubmit}
              className="p-button-sm p-button-success"
            />
          </div>
        }
      >
        <div className="p-field">
          <label htmlFor="newGroupId">ID</label>
          <InputText
            id="newGroupId"
            value={newGroupId}
            onChange={e => setNewGroupId(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="p-field">
          <label htmlFor="newGroupHeader">{T("word.header")}</label>
          <InputText
            id="newGroupHeader"
            value={newGroupHeader}
            onChange={e => setNewGroupHeader(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default TemplateEditor;
